import { Component, inject, OnInit } from '@angular/core';
import { MultiFormAccordionService } from '../../../components/multi-form-accordion/multi-form-accordion.service';
import { PpmRepository } from '../../../store/ppm-state/ppm-state.repository';
import { MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { PpmService } from '../ppm.service';
import { ButtonModule } from 'primeng/button';
import { PpmMilestonesService } from '../ppm-milestones.service';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { PpmWorkflowBlocksService } from '../ppm-workflow-blocks.service';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { ContractingAuthorityService } from '@/pages/settings/contracting-authority/contracting-authority.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FileService, NAMESPACE_ENUM } from '@/components/shared/file-list/file-list.service';
import { CommitteeService } from '@/pages/settings/committee/committee.service';
import { PpmInspectContentBlockComponent } from './content-block/content-block.component';
import { BehaviorSubject } from 'rxjs';
import { Ppm } from '@/types';
import { fileUtils } from '@/utils/file.utils';

@Component({
  selector: 'sm-ppm-inspect-plan',
  imports: [CommonModule, ButtonModule, PageHeaderComponent, TranslateModule, PpmInspectContentBlockComponent],
  templateUrl: './inspect-plan.component.html',
  styleUrl: './inspect-plan.component.scss',
})
export class PpmInspectPlanComponent implements OnInit {
  ppmService = inject(PpmService);
  fileService = inject(FileService);
  ppmMilestonesService = inject(PpmMilestonesService);
  ppmWorkflowBlocksService = inject(PpmWorkflowBlocksService);
  committeeService = inject(CommitteeService);
  ppmRepository = inject(PpmRepository);
  multiFormAccordionService = inject(MultiFormAccordionService);
  contractingAuthorityService = inject(ContractingAuthorityService);
  messageService = inject(MessageService);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  translate = inject(TranslateService);

  breadcrumb = [
    {
      label: 'ppm.title',
      routerLink: ['/ppm-current-plans'],
    },
    {
      label: '',
      routerLink: ['ppm-inspect-plan'],
    },
  ];

  id: number = this.activatedRoute.snapshot.params['id'];
  version?: number = this.activatedRoute.snapshot.params['version'];
  loading$ = new BehaviorSubject<boolean>(true);

  controlBlocks?: DynamicBlocks[];
  status = '';
  ppm?: Ppm;

  ngOnInit() {
    this.ppmRepository.getObservable<Ppm>('response').subscribe(response => {
      this.ppm = response;
      this.breadcrumb[1].label = `Plan Actual - ${response.ref}`;
      this.status = response.status;
      this.controlBlocks = this.ppmWorkflowBlocksService.getInspectPpmBlocks({
        refreshCount: response.nbrActualisation,
        status: response.status,
        accessHistory: () => this.router.navigate(['/ppm-version-history', this.id]),
        downloadSigned: this.ppmRepository.getNested('response', 'modelAlfrescoId') ? () => this.downloadModel() : undefined,
        downloadExcelModel: () => this.downloadExcelModel(),
        refresh: () => this.refresh(),
      });
    });
  }

  downloadModel() {
    this.fileService.download(
      {
        filename: this.ppmRepository.getNested('response', 'ref'),
        extension: 'pdf',
        alfresco_id: this.ppmRepository.getNested('response', 'modelAlfrescoId'),
      },
      NAMESPACE_ENUM.PPM_MODEL
    );
  }

  downloadExcelModel() {
    if (!this.ppm?.version) return;
    this.ppmService
      .downloadExcelPpmModel({
        ppmId: Number(this.id),
        ppmVer: this.ppm?.version,
      })
      .subscribe({
        next: blob => {
          const filename = `${this.ppmRepository.getNested('response', 'ref')}_model.xlsx`;
          fileUtils.triggerBlobDownload(blob, filename);
        },
        error: err => {
          console.error('Download model Excel failed', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: err.error.message || this.translate.instant('Erreur lors du téléchargement du modèle Excel'),
          });
        },
      });
  }

  refresh() {
    this.loading$.next(true);

    this.ppmService.refreshPpm(this.id).subscribe({
      next: () => {
        this.router.navigate(['ppm-edit-plan', this.id]);
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('Le PPM a été actualisé'),
        });
        this.loading$.next(false);
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || this.translate.instant("Erreur lors de l'actualisation du PPM"),
        });
        this.loading$.next(false);
      },
    });
  }

  refreshFn = () => {
    this.refresh();
  };
}
